import { useState } from 'react'
import { FieldArray, Form, Formik } from 'formik'
import { Icon } from '@iconify/react'
import { Button, FormikSelectField, Tooltip } from '@pepper/ui'

import CountrySelector from 'container/SEOTools/components/CountrySelector/CountrySelector'
import DomainInput from './DomainInput'
import { extractBrandName } from '../utils'
import schema from '../schema'

import s from './AddDomain.module.css'

type FormValues = {
  mainDomain: string
  competitorUrl?: string[]
  country: SEOCountries
  brandWords: string[]
}

type Props = {
  onSubmit: (values: FormValues) => void
  formValues: FormValues
}

const MAX_COMPETITOR_LENGTH = 5

export const AddDomain = ({ onSubmit, formValues }: Props): JSX.Element => {
  const [isBrandWordsTouched, setIsBrandWordsTouched] = useState(false)
  const [isDomainAccessible, setIsDomainAccessible] = useState(false)

  const handleTextFormatting = (brandWords: string[]) => {
    return [
      ...new Set(brandWords.filter(bw => !!bw).map(bw => bw.toLowerCase())),
    ]
  }

  const handleValidationCompletion = (
    setFieldValue: (field: string, value: unknown) => void,
    domain: string,
    isAccessible: boolean,
    isDirty: boolean
  ) => {
    if (!domain || !isAccessible) {
      setFieldValue('brandWords', [])
    } else if (!isBrandWordsTouched) {
      if (!isDirty) {
        setFieldValue('brandWords', formValues.brandWords)
      } else {
        const brandName = extractBrandName(domain)
        if (brandName) {
          setFieldValue('brandWords', [brandName])
        }
      }
    }
  }

  return (
    <div className={s.wrapper}>
      <Formik<FormValues>
        initialValues={formValues}
        onSubmit={onSubmit}
        validationSchema={schema}
        enableReinitialize={true}
      >
        {({
          values: { mainDomain, brandWords, competitorUrl, country },
          setFieldValue,
          dirty,
        }): React.ReactNode => {
          return (
            <Form
              className={s.form}
              onKeyPress={e => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                }
              }}
            >
              <div className={s.addDomain}>
                <div className={s.header}>
                  <div className={s.headerText}>1. Add a Domain</div>
                </div>
                <div className={s.mainUrlWrapperContainer}>
                  <div className={s.urlHeader}>
                    Your URL <span className={s.requiredText}>*</span>
                  </div>
                  <div className={s.mainUrlWrapper}>
                    <DomainInput
                      name="mainDomain"
                      type="main"
                      onValidationComplete={(domain, isAccessible) => {
                        handleValidationCompletion(
                          setFieldValue,
                          domain,
                          isAccessible,
                          dirty
                        )
                        setIsDomainAccessible(isAccessible)
                      }}
                    />
                    <CountrySelector
                      value={country}
                      setValue={value => {
                        setFieldValue('country', value)
                      }}
                      className={s.countryField}
                    />
                  </div>
                </div>
                <div className={s.brandWrapper}>
                  <div className={s.urlHeader}>
                    Branded Keywords <span className={s.requiredText}>*</span>
                    <Tooltip
                      placement="rightTop"
                      title="Unique terms that people use to search for your brand or product. Please note that this cannot be edited later."
                    >
                      <Icon icon="lucide:info" />
                    </Tooltip>
                  </div>

                  <div className={s.bandUrlWrapper}>
                    <FormikSelectField
                      showError={mainDomain.length > 0}
                      fieldProps={{
                        customTag: s.tag,
                        customTagLabel: s.tagLabel,
                        open: false,
                        mode: 'tags',
                        disabled: !isDomainAccessible,
                        tokenSeparators: [','],
                        onBlur: () => setIsBrandWordsTouched(true),
                        closeIcon: (
                          <Icon
                            icon="lucide:x"
                            className={s.tagClose}
                            width={16}
                            height={16}
                          />
                        ),
                        textFormat: handleTextFormatting,
                      }}
                      name="brandWords"
                    />
                  </div>
                </div>
                <div className={s.urlWrapper}>
                  <div className={s.urlHeader}>
                    Competitor URLs{' '}
                    <span className={s.optional}>(Optional)</span>
                  </div>
                  <FieldArray name="competitorUrl">
                    {({ insert: _insert, remove, push }) => (
                      <>
                        <div className={s.competitorUrlWrapper}>
                          {!!competitorUrl?.length &&
                            competitorUrl?.map((url, index) => (
                              <div
                                key={`competitorUrl.${index}`}
                                className={s.eachUrl}
                              >
                                <div className={s.urlIndex}>{index + 1}</div>
                                <DomainInput
                                  name={`competitorUrl.${index}`}
                                  type="competitor"
                                  disabled={mainDomain.length === 0}
                                />
                                {url && (
                                  <Button
                                    onClick={event => {
                                      event.stopPropagation()
                                      remove(index)
                                    }}
                                    type="text"
                                    className={s.deleteUrlBtn}
                                  >
                                    <Icon icon="lucide:trash-2" width={14} />
                                  </Button>
                                )}
                              </div>
                            ))}
                        </div>
                        {(competitorUrl?.length ?? 0) <
                          MAX_COMPETITOR_LENGTH && (
                          <Button
                            type="link"
                            onClick={() => push('')}
                            className={s.addCompetitorBtn}
                          >
                            <Icon
                              icon="lucide:plus"
                              width={14}
                              className={s.addCompetitorBtnIcon}
                            />
                            Add a competitor
                          </Button>
                        )}
                      </>
                    )}
                  </FieldArray>
                </div>
              </div>

              <div className={s.footer}>
                <Button
                  type="primary"
                  className={s.nextBtn}
                  htmlType="submit"
                  disabled={
                    mainDomain.length === 0 ||
                    brandWords.length === 0 ||
                    !isDomainAccessible
                  }
                >
                  Next
                </Button>
              </div>
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}
